import type { MechanicDefinition } from "./types";

export const timeline: MechanicDefinition = {
  id: "timeline",
  disabled: true,
  name: "Story Timeline",
  description: "Chronological event tracker — logs key moments, dates, and milestones to give players and AI temporal context",
  icon: "CalendarClock",
  category: "systems",
  aiHints: {
    purpose: "Logs key story events chronologically so both players and AI have temporal context for the narrative.",
    whenToUse: "Long campaigns, mystery/investigation games, or any world where tracking 'when things happened' matters.",
    synergies: ["time_passage", "quest_journal", "reactive_world"],
    conflicts: [],
    prerequisites: [],
    tags: ["timeline", "chronology", "events", "history", "tracking"],
    auditPasses: 1,
    complexity: 2,
    exampleConfig: { auto_log: true, show_dates: true },
    stateVariables: ["_timeline_events"],
  },
  configSchema: [
    {
      key: "time_format",
      label: "Time Format",
      type: "select",
      options: [
        { value: "turns", label: "Turn-based (T=1, T=2...)" },
        { value: "relative", label: "Relative (hours/days since start)" },
        { value: "calendar", label: "In-world calendar dates" },
      ],
      default: "turns",
      description: "How time is displayed on the timeline",
    },
    {
      key: "calendar_epoch",
      label: "Calendar Start (flavor text)",
      type: "text",
      default: "Day 1",
      description: "Starting date label for the calendar (e.g. '15th of Tammuz, Year 3' or 'March 44 BC')",
    },
    {
      key: "auto_log",
      label: "Auto-Log Events",
      type: "boolean",
      default: true,
      description: "Automatically log significant events (discoveries, combat, quest changes)",
    },
    {
      key: "max_visible",
      label: "Max Visible Events",
      type: "number",
      default: 50,
      description: "How many events to display in the timeline widget",
    },
  ],
  systemPromptSection: (config) => {
    const format = config.time_format || "turns";
    const epoch = config.calendar_epoch || "Day 1";
    const autoLog = config.auto_log !== false;

    const formatDesc: Record<string, string> = {
      turns: "Events are indexed by turn number (T=0, T=1, ...). Reference turns when recalling past events.",
      relative: "Events are tracked by elapsed time since story start (e.g. '2 hours in', 'Day 3'). Use advance_time if available.",
      calendar: `Events use in-world calendar dates starting from "${epoch}". Advance dates narratively and log them.`,
    };

    return `## STORY TIMELINE (Rules — Chronological Tracker)
- A persistent timeline tracks all significant story events in chronological order.
- Format: ${format}. ${formatDesc[format]}
- Use the "log_timeline_event" tool to record key moments: discoveries, arrivals, departures, battles, decisions, deaths, meetings.
- Each event has: timestamp/turn, title (short), description (1-2 sentences), category, and optionally linked entity names.
- Categories: "discovery", "combat", "travel", "social", "quest", "death", "milestone", "custom".
${autoLog ? "- AUTO-LOG: After resolving significant actions, automatically log a timeline event. Don't log trivial actions (looking around, small talk)." : "- Manual logging only — log events when explicitly significant."}
- When referencing past events in narration, cite the timeline for consistency (e.g. "three days ago when you arrived at...").
- The timeline is visible to players — it serves as a shared story recap.
- Codex entities may reference timeline events. When revealing or changing entities, consider logging the moment.
- Use "remove_timeline_event" to correct mistakes or retcon events if needed.`;
  },
  widgets: [
    {
      id: "timeline_tab",
      type: "sidebar_tab",
      label: "Timeline",
      icon: "CalendarClock",
      component: "TimelineWidget",
      dataBindings: {
        events: "variables.timeline_events",
        timeFormat: "variables.timeline_format",
        calendarEpoch: "variables.timeline_epoch",
      },
      visibility: "when_active",
      priority: 6,
    },
    {
      id: "timeline_status",
      type: "status_bar_item",
      label: "Events",
      icon: "CalendarClock",
      component: "TimelineStatus",
      dataBindings: {
        eventCount: "variables.timeline_event_count",
        lastEvent: "variables.timeline_last_event",
      },
      visibility: "when_data",
      priority: 8,
    },
  ],
  initializeState: (config) => ({
    timeline_events: [],
    timeline_format: config.time_format || "turns",
    timeline_epoch: config.calendar_epoch || "Day 1",
    timeline_event_count: 0,
    timeline_last_event: null,
  }),
  tools: [
    {
      type: "function",
      function: {
        name: "log_timeline_event",
        description:
          "Log a significant story event to the chronological timeline. Use for discoveries, combat outcomes, arrivals, key decisions, deaths, and milestones.",
        parameters: {
          type: "object",
          properties: {
            title: {
              type: "string",
              description: "Short title for the event (3-8 words)",
            },
            description: {
              type: "string",
              description: "Brief description of what happened (1-2 sentences)",
            },
            category: {
              type: "string",
              enum: ["discovery", "combat", "travel", "social", "quest", "death", "milestone", "custom"],
              description: "Event category for filtering and display",
            },
            timestamp_label: {
              type: "string",
              description:
                "Human-readable time label. For turns: 'Turn 5'. For relative: '2 hours in'. For calendar: '15th of Tammuz'. Must match the configured format.",
            },
            linked_entities: {
              type: "array",
              items: { type: "string" },
              description: "Names of entities involved in this event (NPCs, locations, items)",
            },
            importance: {
              type: "number",
              description: "1-10 importance rating. 1=minor, 5=notable, 10=world-changing",
            },
          },
          required: ["title", "description", "category", "timestamp_label"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "remove_timeline_event",
        description: "Remove a timeline event by title (for corrections or retcons).",
        parameters: {
          type: "object",
          properties: {
            title: {
              type: "string",
              description: "Exact title of the event to remove",
            },
          },
          required: ["title"],
        },
      },
    },
  ],
  onEvent: (event, context) => {
    // Auto-log combat kills, quest completions, etc.
    const autoLog = context.config?.auto_log !== false;
    if (!autoLog) return null;

    const events = [...(context.state.timeline_events || [])];
    let newEvent: any = null;

    if (event.type === "combat_kill") {
      newEvent = {
        title: `Defeated ${event.data.target || "enemy"}`,
        description: event.data.description || `Combat encounter resolved.`,
        category: "combat",
        turn: context.state.turn_count || 0,
        timestamp_label: `Turn ${context.state.turn_count || 0}`,
        importance: event.data.importance || 5,
        linked_entities: [event.data.target].filter(Boolean),
      };
    } else if (event.type === "quest_complete") {
      newEvent = {
        title: `Quest: ${event.data.quest_title || "Unknown"}`,
        description: event.data.description || "Quest completed.",
        category: "quest",
        turn: context.state.turn_count || 0,
        timestamp_label: `Turn ${context.state.turn_count || 0}`,
        importance: event.data.importance || 7,
        linked_entities: event.data.entities || [],
      };
    }

    if (newEvent) {
      events.push(newEvent);
      return {
        stateUpdates: {
          timeline_events: events,
          timeline_event_count: events.length,
          timeline_last_event: newEvent.title,
        },
        narrativeNotes: [`[Timeline: "${newEvent.title}" logged]`],
      };
    }

    return null;
  },
};
